import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../src/contexts/AuthContext';
import { gsap } from 'gsap';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
      return;
    }

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out', duration: 0.8 } });
      tl.fromTo(cardRef.current,
        { y: 30, opacity: 0, scale: 0.98 },
        { y: 0, opacity: 1, scale: 1, delay: 0.2 }
      )
        .fromTo(formRef.current?.children || [],
          { y: 15, opacity: 0 },
          { y: 0, opacity: 1, stagger: 0.08, ease: 'power2.out' },
          "-=0.4"
        );
    }, containerRef);

    return () => ctx.revert();
  }, [isAuthenticated, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Las contraseñas no coinciden', customClass: { popup: 'rounded-2xl' } });
      return;
    }

    const phone = formData.phone.trim();
    if (!/^\d{10}$/.test(phone)) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'El teléfono debe tener exactamente 10 dígitos', customClass: { popup: 'rounded-2xl' } });
      return;
    }

    try {
      setIsSubmitting(true);
      await register({
        name: formData.name,
        phone: phone,
        password: formData.password,
      });
      navigate('/');
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err instanceof Error ? err.message : 'Error al registrar el usuario. Intenta nuevamente.', customClass: { popup: 'rounded-2xl' } });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={containerRef} className="min-h-screen relative overflow-hidden bg-background-light dark:bg-background-dark flex items-center justify-center p-6 py-12 ">
      <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] bg-primary/10 rounded-full blur-[120px] pointer-events-none "></div>
      <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] bg-orange-500/10 rounded-full blur-[120px] pointer-events-none "></div>

      <div className="w-full max-w-md relative z-10 dark:bg-slate-800/50 dark:rounded-[2.5rem] bg-gray-200 rounded-[2.5rem] shadow-xl">
        <div ref={cardRef} className="glass-card rounded-[2.5rem] p-8 md:p-10">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="absolute top-6 left-6 w-10 h-10 rounded-full bg-gray-100 dark:bg-white/10 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/20 transition-colors"
          >
            <span className="material-symbols-outlined text-gray-600 dark:text-gray-300">close</span>
          </button>
          <div className="text-center mb-10">
            <h1 className="text-3xl font-black text-secondary dark:text-white tracking-tight mb-2">Crear Cuenta</h1>
            <p className="text-gray-500 dark:text-gray-400 font-medium">Únete a la mejor experiencia culinaria</p>
          </div>

          <form ref={formRef} className="space-y-4 max-w-sm mx-auto" onSubmit={handleSubmit}>

            <div className="space-y-1.5 text-center">
              <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Nombre Completo</label>
              <div className="relative group">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400 group-focus-within:text-primary transition-colors">person</span>
                <input name="name" type="text" required value={formData.name} onChange={handleChange} className="input-premium pl-12 rounded-full bg-white dark:bg-slate-700" placeholder="Tu nombre" />
              </div>
            </div>

            <div className="space-y-1.5 text-center">
              <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Número de Teléfono</label>
              <div className="relative group">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400 group-focus-within:text-primary transition-colors">phone</span>
                <input
                  name="phone"
                  type="tel"
                  required
                  maxLength={10}
                  pattern="\d{10}"
                  inputMode="numeric"
                  value={formData.phone}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData(prev => ({ ...prev, phone: val }));
                  }}
                  className="input-premium pl-12 rounded-full"
                  placeholder="10 dígitos"
                />
              </div>
            </div>

            <div className="space-y-1.5 text-center">
              <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Contraseña</label>
              <div className="relative group">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400 group-focus-within:text-primary transition-colors">lock</span>
                <input name="password" type="password" required value={formData.password} onChange={handleChange} className="input-premium pl-12 rounded-full" placeholder="••••••••" />
              </div>
            </div>

            <div className="space-y-1.5 text-center">
              <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Confirmar Contraseña</label>
              <div className="relative group">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400 group-focus-within:text-primary transition-colors">verified_user</span>
                <input name="confirmPassword" type="password" required value={formData.confirmPassword} onChange={handleChange} className="input-premium pl-12 rounded-full bg-white dark:bg-slate-700" placeholder="••••••••" />
              </div>
            </div>

            <div className="pt-6 mx-auto max-w-xs">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary-premium w-full group overflow-hidden relative"
              >
                <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 pointer-events-none"></div>
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <span className="font-bold">Registrarme</span>
                )}
              </button>
            </div>
          </form>

          <p className="mt-8 text-center text-sm font-medium text-gray-500 dark:text-gray-400">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-primary font-bold hover:underline">Inicia Sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
